/**
 * The diurnal QC figure as SVG (spec docs/specs/diurnal-qc.md §6.1): one drawing for the screenshot JPG, the
 * clipboard copy, the daily PDF and Teslyn Cloud's Diurnal QC page and published day (moved here from Teslyn Field for
 * parity C3), so they always match (ADR-007; "one consistent method"). Pure: data in, SVG text
 * out.
 *
 * The Diurnal tool's screenshot (decision 56, Q29; decisions 252, 255, 256): tenant name and title at the top, the
 * project number and base, "Julian Day n"; the settings beside each chart as the tool shows them (start and end time
 * blank, no Y scales); one diurnal chart holding every dataset, each on its own axis in the tool's colours (main
 * dataset black, other basemags blue, grey, lightblue; TA Live greens, GA olives; an external screenshot draws the
 * green and olive lines black); survey lines as a green start line, a red end line and "Line n"; exceedances in red;
 * no gap or bad-data markers; each shown metric chart on its Y scale; "Loaded data format" under the charts.
 */
import { chartIndices, METRIC_LABEL, metricThreshold } from './diurnal-qc.js';
import type { DiurnalMetric, DiurnalQc, DiurnalSettings } from './diurnal-qc.js';
import {
  DIURNAL_COLOUR_VALUES,
  datasetColours,
  diurnalAutoRange,
  diurnalDrawOrder,
  metricAxis,
  normalisedRanges,
  Y_SCALE_DEFAULT,
  type DiurnalColour,
  type DiurnalFamily,
  type ScreenshotUse,
} from './diurnal-chart.js';
import type { DiurnalLine } from './basemag.js';
import type { MltBand } from './diurnal-mlt.js';
import { localParts, utcToLocalMs } from './time-model.js';

/** A Diurnal QC colour's value (decision 256; `brand/tokens/tokens.json` group `diurnal`). */
const diurnalColour = (name: DiurnalColour): string => DIURNAL_COLOUR_VALUES[name];

const SANS = 'Archivo, Arial, sans-serif';
const MONO = 'IBM Plex Mono, Consolas, monospace';

export interface FigureDataset {
  readonly label: string;
  /** QC of the series as drawn (the Power User copy when clean-up is on). */
  readonly qc: DiurnalQc;
  /** Colour family (basemag when absent). */
  readonly family?: DiurnalFamily;
}

export interface FigureInput {
  readonly tenantName: string;
  readonly project: string;
  readonly base: string;
  readonly offsetH: number;
  readonly datasets: readonly FigureDataset[];
  /** The main dataset: black, drawn on top, and the one whose metric charts are drawn. */
  readonly primary: number;
  readonly lines: readonly DiurnalLine[];
  readonly settings: DiurnalSettings;
  /** Local-time window to draw (epoch-style local ms); the trimmed data range when absent. */
  readonly window?: { readonly start: number; readonly end: number } | null;
  readonly width?: number;
  /** Logo image as a data URL (PNG), drawn top left. */
  readonly logo?: string | null;
  /**
   * Internal keeps the TA Live greens and GA olives; external draws them black. The screen leaves observatory data
   * out of an External figure before it gets here (decisions 340, 341: `figureDatasets`).
   */
  readonly use?: ScreenshotUse;
  /** Each metric chart's Y scale, nT (12 when absent). */
  readonly yScale?: Partial<Readonly<Record<DiurnalMetric, number>>>;
  /** Normalise Y across datasets, as on the screen. */
  readonly normalise?: boolean;
  /** The "Select Julian day" box: "All" or the day chosen. */
  readonly daySelect?: string;
  /** The main dataset's format, written under the charts ("Loaded data format: …"). */
  readonly formatLabel?: string;
  /** The MLT risk windows shaded on every chart while the overlay is on (sign-off 02-040; local seconds). */
  readonly mlt?: readonly MltBand[];
  /**
   * Written under the charts when a dataset drawn is its edited readings (the superuser data tools,
   * docs/specs/basemag-edits.md §5): "Edited readings: …".
   */
  readonly editedNote?: string | null;
}

const c = diurnalColour;
const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const r2 = (v: number): string => (Math.round(v * 100) / 100).toString();

function text(
  x: number,
  y: number,
  s: string,
  o: {
    size?: number;
    anchor?: 'start' | 'middle' | 'end';
    font?: string;
    fill?: string;
    rotate?: number;
    weight?: string;
  } = {},
): string {
  const rot = o.rotate ? ` transform="rotate(${o.rotate} ${r2(x)} ${r2(y)})"` : '';
  const w = o.weight ? ` font-weight="${o.weight}"` : '';
  return `<text x="${r2(x)}" y="${r2(y)}" font-family="${o.font ?? SANS}" font-size="${o.size ?? 14}" text-anchor="${o.anchor ?? 'start'}" fill="${o.fill ?? c('diurnal-label')}"${w}${rot}>${esc(s)}</text>`;
}

/** Nice tick step for a time span (minutes). */
function timeStep(spanMs: number): number {
  const steps = [1, 2, 5, 10, 15, 30, 60, 120, 180, 360, 720];
  const target = spanMs / 60_000 / 12;
  return (steps.find((s) => s >= target) ?? 720) * 60_000;
}

/** Nice value ticks between lo and hi. */
export function valueTicks(lo: number, hi: number, count = 6): number[] {
  const span = hi - lo || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step)
    out.push(Math.round(v / step) * step || 0); // never "-0"
  return out;
}

interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

const fmtTime = (localMs: number): string => {
  const d = new Date(localMs);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

function frame(b: Box, title: string): string {
  return (
    `<rect x="${r2(b.x)}" y="${r2(b.y)}" width="${r2(b.w)}" height="${r2(b.h)}" rx="6" fill="${c('diurnal-background')}" stroke="${c('diurnal-grid')}"/>` +
    text(b.x + b.w / 2, b.y + 24, title, { size: 17, anchor: 'middle' })
  );
}

interface Plot {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
  readonly tMin: number;
  readonly tMax: number;
  readonly vMin: number;
  readonly vMax: number;
}

const px = (p: Pick<Plot, 'x0' | 'x1' | 'tMin' | 'tMax'>, t: number): number =>
  p.x0 + ((t - p.tMin) / (p.tMax - p.tMin || 1)) * (p.x1 - p.x0);
const py = (p: Plot, v: number): number => p.y1 - ((v - p.vMin) / (p.vMax - p.vMin || 1)) * (p.y1 - p.y0);

/** The MLT bands of a plot, behind its readings, clipped to its time range. */
function mltShading(p: Plot, bands: readonly MltBand[] | undefined): string {
  const out: string[] = [];
  for (const b of bands ?? []) {
    const a = Math.max(b.start * 1000, p.tMin);
    const e = Math.min(b.end * 1000, p.tMax);
    if (e <= a) continue;
    out.push(
      `<rect x="${r2(px(p, a))}" y="${r2(p.y0)}" width="${r2(px(p, e) - px(p, a))}" height="${r2(p.y1 - p.y0)}" fill="${c(b.colour)}"/>`,
    );
  }
  return out.join('');
}

function timeAxis(p: Plot): string {
  const out: string[] = [];
  const step = timeStep(p.tMax - p.tMin);
  for (let t = Math.ceil(p.tMin / step) * step; t <= p.tMax; t += step) {
    const x = px(p, t);
    out.push(
      `<line x1="${r2(x)}" x2="${r2(x)}" y1="${r2(p.y0)}" y2="${r2(p.y1)}" stroke="${c('diurnal-grid')}" stroke-width="1"/>`,
    );
    out.push(text(x, p.y1 + 18, fmtTime(t), { size: 12, anchor: 'middle', font: MONO }));
  }
  return out.join('');
}

const AXIS_W = 85;
const LEFT_AXIS = 110;

/** A value axis on the left (with the grid) or on the right of the plot, `slot` axes out from its edge. */
function valueAxis(
  p: Plot,
  ticks: readonly number[],
  digits: number,
  o: { side: 'left' | 'right'; slot: number; colour: string; label?: string; grid: boolean },
): string {
  const out: string[] = [];
  const x = o.side === 'left' ? p.x0 : p.x1 + o.slot * AXIS_W;
  for (const v of ticks) {
    const y = py(p, v);
    if (y < p.y0 - 0.5 || y > p.y1 + 0.5) continue;
    if (o.grid)
      out.push(
        `<line x1="${r2(p.x0)}" x2="${r2(p.x1)}" y1="${r2(y)}" y2="${r2(y)}" stroke="${c('diurnal-grid')}" stroke-width="1"/>`,
      );
    const label = v.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    out.push(
      o.side === 'left'
        ? text(x - 8, y + 4, label, { size: 12, anchor: 'end', font: MONO, fill: o.colour })
        : text(x + 8, y + 4, label, { size: 12, anchor: 'start', font: MONO, fill: o.colour }),
    );
  }
  if (o.label)
    out.push(
      text(o.side === 'left' ? x - 76 : x + 76, (p.y0 + p.y1) / 2, o.label, {
        size: 12,
        anchor: 'middle',
        rotate: -90,
        fill: o.colour,
      }),
    );
  return out.join('');
}

/** A polyline over the kept indices, broken where a value is missing. */
function path(p: Plot, t: (i: number) => number, v: (i: number) => number, idx: readonly number[]): string {
  const parts: string[] = [];
  let open = false;
  for (const i of idx) {
    const y = v(i);
    if (!Number.isFinite(y)) {
      open = false;
      continue;
    }
    parts.push(`${open ? 'L' : 'M'}${r2(px(p, t(i)))} ${r2(py(p, Math.min(p.vMax, Math.max(p.vMin, y))))}`);
    open = true;
  }
  return parts.join('');
}

/** Runs of consecutive kept indices whose reading is flagged. */
function runsOf(idx: readonly number[], on: (i: number) => boolean): number[][] {
  const out: number[][] = [];
  let cur: number[] = [];
  for (const i of idx) {
    if (on(i)) cur.push(i);
    else if (cur.length) {
      out.push(cur);
      cur = [];
    }
  }
  if (cur.length) out.push(cur);
  return out;
}

/** The readings drawn between tMin and tMax: thinned on `of` (a metric panel's own values, R56), else the readings. */
function visible(
  qc: DiurnalQc,
  offsetH: number,
  tMin: number,
  tMax: number,
  of: ArrayLike<number> = qc.values,
): number[] {
  return chartIndices(qc, undefined, of).filter((i) => {
    const t = utcToLocalMs(qc.tMs[i]!, offsetH);
    return t >= tMin && t <= tMax;
  });
}

function extent(values: ArrayLike<number>, idx?: readonly number[]): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  const n = idx ? idx.length : values.length;
  for (let k = 0; k < n; k++) {
    const v = values[idx ? idx[k]! : k]!;
    if (!Number.isFinite(v)) continue;
    lo = Math.min(lo, v);
    hi = Math.max(hi, v);
  }
  return [lo, hi];
}

const familiesOf = (input: FigureInput): DiurnalFamily[] => input.datasets.map((d) => d.family ?? 'basemag');

function diurnalPanel(b: Box, input: FigureInput, tMin: number, tMax: number): string {
  const ds = input.datasets;
  const families = familiesOf(input);
  const colours = datasetColours(families, input.primary, input.use ?? 'internal').map((n) => c(n));
  const idxs = ds.map((d) => visible(d.qc, input.offsetH, tMin, tMax));
  const norm = input.normalise
    ? normalisedRanges(
        ds.map((d, k) => {
          const [lo, hi] = extent(d.qc.values);
          return { family: families[k]!, lo, hi };
        }),
      )
    : null;
  const x = { x0: b.x + LEFT_AXIS, x1: b.x + b.w - 30 - Math.max(0, ds.length - 1) * AXIS_W, tMin, tMax };
  const y = { y0: b.y + 44, y1: b.y + b.h - 40 };
  const plots: Plot[] = ds.map((d, k) => {
    if (norm) {
      const a = norm.axes[k]!;
      return { ...x, ...y, vMin: a.min, vMax: a.max };
    }
    const [vMin, vMax] = diurnalAutoRange(...extent(d.qc.values, idxs[k]));
    return { ...x, ...y, vMin, vMax };
  });
  const first = plots[0] ?? { ...x, ...y, vMin: 0, vMax: 1 };
  const out: string[] = [frame(b, 'Diurnal'), mltShading(first, input.mlt), timeAxis(first)];
  // axes: the main dataset on the left (with the grid), the others on the right in turn, each in its colour
  let slot = 0;
  ds.forEach((d, k) => {
    const p = plots[k]!;
    const main = k === input.primary;
    const off = norm?.axes[k]?.offset ?? 0;
    const shifted = { ...p, vMin: p.vMin - off, vMax: p.vMax - off };
    out.push(
      valueAxis(shifted, valueTicks(shifted.vMin, shifted.vMax), 0, {
        side: main ? 'left' : 'right',
        slot: main ? 0 : slot++,
        colour: colours[k]!,
        label: d.label,
        grid: main,
      }),
    );
  });
  // survey lines: green start, red end, "Line n" in black at the middle (the tool's createSurveyLinePlugin)
  for (const l of input.lines) {
    const s = utcToLocalMs(Date.parse(l.start_at), input.offsetH);
    const e = utcToLocalMs(Date.parse(l.end_at), input.offsetH);
    if (e < tMin || s > tMax) continue;
    for (const [t, colour] of [
      [s, c('diurnal-line-start')],
      [e, c('diurnal-line-end')],
    ] as const) {
      if (t < tMin || t > tMax) continue;
      const lx = px(x, t);
      out.push(
        `<line x1="${r2(lx)}" x2="${r2(lx)}" y1="${r2(y.y0)}" y2="${r2(y.y1)}" stroke="${colour}" stroke-width="1"/>`,
      );
    }
    const mid = (s + e) / 2;
    if (mid >= tMin && mid <= tMax)
      out.push(
        text(px(x, mid) + 4, (y.y0 + y.y1) / 2, `Line ${l.line}`, {
          size: 12,
          anchor: 'middle',
          rotate: -90,
          weight: '700',
        }),
      );
  }
  // the datasets, the main one last (on top), each with its exceedances in red; no gap or bad-data markers (255)
  for (const k of diurnalDrawOrder(ds.length, input.primary)) {
    const d = ds[k]!;
    const p = plots[k]!;
    const off = norm?.axes[k]?.offset ?? 0;
    const t = (i: number): number => utcToLocalMs(d.qc.tMs[i]!, input.offsetH);
    const v = (i: number): number => d.qc.values[i]! + off;
    out.push(
      `<path d="${path(p, t, v, idxs[k]!)}" fill="none" stroke="${colours[k]}" stroke-width="1" stroke-linejoin="round"/>`,
    );
    for (const run of runsOf(idxs[k]!, (i) => d.qc.exceed[i]! !== 0))
      out.push(
        `<path d="${path(p, t, v, run)}" fill="none" stroke="${c('diurnal-exceedance')}" stroke-width="5" stroke-linecap="round"/>`,
      );
  }
  return out.join('');
}

/** Chart titles as on the screen (the tool's, in sentence case). */
export function metricTitle(m: DiurnalMetric, s: DiurnalSettings): string {
  const mode = s.plotMode === 'peakToPeak' ? 'Peak to peak' : 'Deviation from chord';
  if (m === 'plot1' || m === 'plot2') return `${mode} ${METRIC_LABEL[m]}`;
  if (m === 'dor') return 'Deviation of residuals';
  if (m === 'pcal') return 'Pcal gradient';
  return 'Gradient';
}

function metricPanel(
  b: Box,
  qc: DiurnalQc,
  m: DiurnalMetric,
  input: FigureInput,
  colour: string,
  tMin: number,
  tMax: number,
): string {
  const values = qc[m];
  if (!values) return '';
  const s = input.settings;
  const thr = metricThreshold(s, m);
  const axis = metricAxis(m, input.yScale?.[m] ?? Y_SCALE_DEFAULT, thr);
  const idx = visible(qc, input.offsetH, tMin, tMax, values);
  const p: Plot = {
    x0: b.x + LEFT_AXIS,
    x1: b.x + b.w - 30 - Math.max(0, input.datasets.length - 1) * AXIS_W,
    y0: b.y + 40,
    y1: b.y + b.h - 34,
    tMin,
    tMax,
    vMin: axis.min,
    vMax: axis.max,
  };
  const t = (i: number): number => utcToLocalMs(qc.tMs[i]!, input.offsetH);
  const out = [
    frame(b, metricTitle(m, s)),
    mltShading(p, input.mlt),
    timeAxis(p),
    valueAxis(p, axis.ticks, axis.decimals, {
      side: 'left',
      slot: 0,
      colour: c('diurnal-label'),
      grid: true,
    }),
  ];
  for (const level of m === 'pcal' ? [thr, -thr] : [thr]) {
    if (level < axis.min || level > axis.max) continue;
    const ly = py(p, level);
    out.push(
      `<line x1="${r2(p.x0)}" x2="${r2(p.x1)}" y1="${r2(ly)}" y2="${r2(ly)}" stroke="${c('diurnal-threshold')}" stroke-width="2"/>`,
    );
  }
  out.push(
    `<path d="${path(p, t, (i) => values[i]!, idx)}" fill="none" stroke="${colour}" stroke-width="1"/>`,
  );
  for (const run of runsOf(
    idx,
    (i) => Number.isFinite(values[i]!) && Math.abs(values[i]!) > thr && i >= qc.trim.start && i < qc.trim.end,
  )) {
    out.push(
      `<path d="${path(p, t, (i) => values[i]!, run)}" fill="none" stroke="${c('diurnal-exceedance')}" stroke-width="5" stroke-linecap="round"/>`,
    );
  }
  return out.join('');
}

/** The metric charts drawn: Plot 1 always, the others while shown, in the tool's order. */
export function shownMetrics(s: DiurnalSettings): DiurnalMetric[] {
  return (['gradient', 'pcal', 'dor', 'plot1', 'plot2'] as const).filter((m) => m === 'plot1' || s.show[m]);
}

interface Rows {
  readonly group: string;
  readonly rows: readonly (readonly [string, string])[];
}

/**
 * The settings beside each chart, as the tool's screenshot shows them (index.html chart blocks; decision 255):
 * start and end time always blank, the day box and the UTC offset beside the diurnal chart; each metric's threshold
 * and window beside its chart. The Y scales are hidden, as the tool hides them.
 */
export function settingsRows(
  s: DiurnalSettings,
  offsetH: number,
  daySelect = 'All',
): { time: Rows; metrics: Readonly<Record<DiurnalMetric, Rows>> } {
  return {
    time: {
      group: 'Time settings',
      rows: [
        ['Start time (HH:MM)', ''],
        ['End time (HH:MM)', ''],
        ['Select Julian day', daySelect],
        ['UTC offset (hours)', String(offsetH)],
      ],
    },
    metrics: {
      gradient: {
        group: 'Gradient settings',
        rows: [
          ['Gradient nT', String(s.gradientThresholdNt)],
          ['Gradient (min)', String(s.gradientWindowMin)],
        ],
      },
      pcal: { group: 'Pcal gradient settings', rows: [['Pcal nT', String(s.pcalThresholdNt)]] },
      dor: {
        group: 'Deviation of residuals',
        rows: [
          ['Threshold (nT)', String(s.dorThresholdNt)],
          ['Deviation (min)', String(s.dorWindowMin)],
        ],
      },
      plot1: {
        group: 'Plot 1 settings',
        rows: [
          ['Plot 1 nT', String(s.plot1ThresholdNt)],
          ['Plot 1 (min)', String(s.plot1WindowMin)],
        ],
      },
      plot2: {
        group: 'Plot 2 settings',
        rows: [
          ['Plot 2 nT', String(s.plot2ThresholdNt)],
          ['Plot 2 (min)', String(s.plot2WindowMin)],
        ],
      },
    },
  };
}

function settingsBlock(g: Rows, x: number, y: number, w: number): string {
  const out = [text(x + w / 2, y + 30, g.group, { size: 18, anchor: 'middle', weight: '600' })];
  let sy = y + 64;
  for (const [label, value] of g.rows) {
    out.push(text(x + w - 120, sy, label, { size: 14, anchor: 'end', weight: '600' }));
    out.push(
      `<rect x="${r2(x + w - 108)}" y="${r2(sy - 22)}" width="84" height="32" rx="3" fill="${c('diurnal-background')}" stroke="${c('diurnal-threshold')}"/>`,
    );
    if (value)
      out.push(text(x + w - 66, sy, value, { size: 16, anchor: 'middle', font: MONO, weight: '600' }));
    sy += 44;
  }
  return out.join('');
}

/** Julian days covered by the drawn window, "Julian Day 79" or "Julian Days 53 to 54". */
export function jdayHeading(input: FigureInput, range: { start: number; end: number }): string {
  const a = localParts(range.start - input.offsetH * 3_600_000, input.offsetH).jday;
  const b = localParts(range.end - input.offsetH * 3_600_000, input.offsetH).jday;
  return a === b ? `Julian Day ${a}` : `Julian Days ${a} to ${b}`;
}

/** The local-time range drawn: the window, else the trimmed span of every dataset. */
export function figureRange(input: FigureInput): { start: number; end: number } {
  if (input.window) return { start: input.window.start, end: input.window.end };
  let s = Infinity;
  let e = -Infinity;
  for (const d of input.datasets) {
    const q = d.qc;
    if (q.trim.end <= q.trim.start) continue;
    s = Math.min(s, utcToLocalMs(q.tMs[q.trim.start]!, input.offsetH));
    e = Math.max(e, utcToLocalMs(q.tMs[q.trim.end - 1]!, input.offsetH));
  }
  if (!Number.isFinite(s)) return { start: 0, end: 1 };
  // the MLT overlay widens the range to its last band, as on the screen
  for (const b of input.mlt ?? []) e = Math.max(e, b.end * 1000);
  return { start: s, end: e };
}

export function renderDiurnalFigureSvg(input: FigureInput): string {
  const W = input.width ?? 2400;
  const range = figureRange(input);
  const left = 300;
  const chartW = W - left - 30;
  const header = 230;
  const diurnalH = 640;
  const metricH = 250;
  const primary = input.datasets[input.primary] ?? input.datasets[0];
  const metrics = primary ? shownMetrics(input.settings) : [];
  const captionH = (input.formatLabel ? 40 : 0) + (input.editedNote ? 30 : 0);
  const H = header + (diurnalH + 16) + metrics.length * (metricH + 16) + captionH + 20;
  const out: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`,
    `<rect width="${W}" height="${H}" fill="${c('diurnal-background')}"/>`,
  ];
  if (input.logo)
    out.push(
      `<image href="${input.logo}" x="24" y="20" height="90" width="260" preserveAspectRatio="xMinYMid meet"/>`,
    );
  out.push(text(W / 2, 60, `${input.tenantName}  Diurnal QC`, { size: 40, anchor: 'middle' }));
  out.push(
    text(W / 2, 130, [input.project, input.base].filter(Boolean).join('        '), {
      size: 30,
      anchor: 'middle',
    }),
  );
  out.push(text(W / 2, 195, jdayHeading(input, range), { size: 30, anchor: 'middle' }));
  const rows = settingsRows(input.settings, input.offsetH, input.daySelect ?? 'All');
  let y = header;
  out.push(settingsBlock(rows.time, 0, y, left - 10));
  if (input.datasets.length > 0)
    out.push(diurnalPanel({ x: left, y, w: chartW, h: diurnalH }, input, range.start, range.end));
  y += diurnalH + 16;
  if (primary) {
    const k = input.datasets.indexOf(primary);
    const colour = c(datasetColours(familiesOf(input), k, input.use ?? 'internal')[k]!);
    for (const m of metrics) {
      out.push(settingsBlock(rows.metrics[m], 0, y, left - 10));
      out.push(
        metricPanel(
          { x: left, y, w: chartW, h: metricH },
          primary.qc,
          m,
          input,
          colour,
          range.start,
          range.end,
        ),
      );
      y += metricH + 16;
    }
  }
  if (input.formatLabel)
    out.push(
      text(left + chartW / 2, y + 22, `Loaded data format: ${input.formatLabel}`, {
        size: 13,
        anchor: 'middle',
        fill: c('diurnal-threshold'),
      }),
    );
  if (input.editedNote)
    out.push(
      text(left + chartW / 2, y + (input.formatLabel ? 52 : 22), input.editedNote, {
        size: 13,
        anchor: 'middle',
        fill: c('diurnal-threshold'),
      }),
    );
  out.push('</svg>');
  return out.join('');
}
